import { useBackendApi } from './backendApi';
import { useObjectDatesMapper } from './Helper';
import { EntityContext } from './llmChatService';
import { Limit, PageResponse } from './Types';

export type LlmMessageRole = 'user' | 'assistant' | 'tool';

export interface LlmConversation {
  id: number;
  title: string;
  model: string | null;
  createdAt: Date;
  updatedAt: Date | null;
  lastMessageAt: Date | null;
  lastMessagePreview: string | null;
}

export interface LlmMessage {
  id: number;
  role: LlmMessageRole;
  content: string | null;
  tokenCount: number;
  toolCalls?: unknown[] | null;
  toolCallId?: string | null;
  context?: EntityContext[] | null;
  createdAt: Date;
}

export interface UpdateConversationRequest {
  title?: string;
}

function useLlmConversationMapper() {
  return useObjectDatesMapper<LlmConversation>([], ['createdAt', 'updatedAt', 'lastMessageAt']);
}

function useLlmMessageMapper() {
  return useObjectDatesMapper<LlmMessage>([], ['createdAt']);
}

export function useLlmConversationsService() {
  const backendApi = useBackendApi();
  const conversationMapper = useLlmConversationMapper();
  const messageMapper = useLlmMessageMapper();

  const getConversations = async (limit?: Limit): Promise<PageResponse<LlmConversation>> => {
    const params = limit
      ? `?limit=${limit.limit}&offset=${limit.offset}`
      : '';
    const response = await backendApi.get(`/conversations${params}`) as PageResponse<LlmConversation>;
    return {
      ...response,
      data: conversationMapper.mapList(response.data),
    };
  };

  const getConversation = async (id: number): Promise<LlmConversation> => {
    const response = await backendApi.get(`/conversations/${id}`) as LlmConversation;
    return conversationMapper.mapSingle(response);
  };

  const getConversationMessages = async (conversationId: number): Promise<LlmMessage[]> => {
    const response = await backendApi.get(`/conversations/${conversationId}/messages`) as LlmMessage[];
    return messageMapper.mapList(response);
  };

  const updateConversation = async (
    id: number,
    request: UpdateConversationRequest,
  ): Promise<LlmConversation> => {
    const response = await backendApi.put(`/conversations/${id}`, request) as LlmConversation;
    return conversationMapper.mapSingle(response);
  };

  const deleteConversation = async (id: number): Promise<void> => {
    await backendApi.delete(`/conversations/${id}`);
  };

  return {
    getConversations,
    getConversation,
    getConversationMessages,
    updateConversation,
    deleteConversation,
  };
}
